import { useLanguage } from "@/LanguageContext";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";

export default function PrivacyPage({ onBack }) {
  const { language } = useLanguage();

  return (
    <div className="min-h-screen bg-[#F0EBE3] pt-28 pb-20" data-testid="privacy-page">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <button
          onClick={onBack}
          className="flex items-center gap-2 font-body text-sm text-[#6FA8B6] hover:text-[#5B8D9A] transition-colors mb-10"
          data-testid="privacy-back-button"
        >
          <ArrowLeft size={16} strokeWidth={1.5} />
          {language === "de" ? "Zurück" : language === "en" ? "Back" : "Retour"}
        </button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-[#2C3E50] mb-12">
            {language === "de" ? "Datenschutzrichtlinie" : language === "en" ? "Privacy Policy" : "Politique de confidentialité"}
          </h1>

          <div className="space-y-10 font-body text-[#2C3E50]/80 leading-relaxed">
            <div>
              <h2 className="font-heading text-xl text-[#2C3E50] mb-3">
                {language === "de" ? "Datenerfassung" : language === "en" ? "Data Collection" : "Collecte des données"}
              </h2>
              <p className="mb-3">
                {language === "de"
                  ? "Wenn Sie diese Website nutzen, können bestimmte Daten erfasst werden:"
                  : language === "en"
                  ? "When you use this site, certain data may be collected:"
                  : "Lorsque vous utilisez ce site, certaines données peuvent être collectées :"}
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>{language === "de" ? "Name" : language === "en" ? "Name" : "Nom"}</li>
                <li>{language === "de" ? "Telefonnummer" : language === "en" ? "Phone number" : "Numéro de téléphone"}</li>
                <li>{language === "de" ? "E-Mail-Adresse" : language === "en" ? "Email address" : "Adresse email"}</li>
                <li>{language === "de" ? "Über das Formular gesendete Nachricht" : language === "en" ? "Message sent via the form" : "Message envoyé via le formulaire"}</li>
              </ul>
              <p className="mt-3">
                {language === "de"
                  ? "Diese Daten werden ausschliesslich zur Beantwortung Ihrer Anfrage verwendet."
                  : language === "en"
                  ? "This data is only used to respond to your request."
                  : "Ces données sont uniquement utilisées pour répondre à votre demande."}
              </p>
            </div>

            <div>
              <h2 className="font-heading text-xl text-[#2C3E50] mb-3">
                {language === "de" ? "Verwendung der Daten" : language === "en" ? "Use of Data" : "Utilisation des données"}
              </h2>
              <p className="mb-3">
                {language === "de"
                  ? "Die gesammelten Daten dienen ausschliesslich dazu:"
                  : language === "en"
                  ? "The collected data is only used to:"
                  : "Les données collectées servent uniquement à :"}
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>{language === "de" ? "Mit Ihnen Kontakt aufzunehmen" : language === "en" ? "Contact you" : "Prendre contact avec vous"}</li>
                <li>{language === "de" ? "Ihre Anfragen zu beantworten" : language === "en" ? "Respond to your requests" : "Répondre à vos demandes"}</li>
                <li>{language === "de" ? "Bei Bedarf eine Begleitung zu organisieren" : language === "en" ? "Organize accompaniment if necessary" : "Organiser un accompagnement si nécessaire"}</li>
              </ul>
              <p className="mt-3">
                {language === "de"
                  ? "Keine Daten werden an Dritte verkauft oder weitergegeben."
                  : language === "en"
                  ? "No data is sold or shared with third parties."
                  : "Aucune donnée n'est vendue ou partagée à des tiers."}
              </p>
            </div>

            <div>
              <h2 className="font-heading text-xl text-[#2C3E50] mb-3">
                {language === "de" ? "Aufbewahrung der Daten" : language === "en" ? "Data Retention" : "Conservation des données"}
              </h2>
              <p>
                {language === "de"
                  ? "Die Daten werden nur so lange aufbewahrt, wie es für die Bearbeitung Ihrer Anfrage erforderlich ist."
                  : language === "en"
                  ? "Data is only retained for the time necessary to process your request."
                  : "Les données sont conservées uniquement le temps nécessaire au traitement de votre demande."}
              </p>
            </div>

            <div>
              <h2 className="font-heading text-xl text-[#2C3E50] mb-3">
                {language === "de" ? "Sicherheit" : language === "en" ? "Security" : "Sécurité"}
              </h2>
              <p>
                {language === "de"
                  ? "Présence & Sourire ergreift angemessene Massnahmen zum Schutz Ihrer Daten."
                  : language === "en"
                  ? "Présence & Sourire implements reasonable measures to protect your data."
                  : "Présence & Sourire met en œuvre des mesures raisonnables pour protéger vos données."}
              </p>
            </div>

            <div>
              <h2 className="font-heading text-xl text-[#2C3E50] mb-3">
                {language === "de" ? "Ihre Rechte" : language === "en" ? "Your Rights" : "Vos droits"}
              </h2>
              <p className="mb-3">
                {language === "de"
                  ? "Gemäss geltendem Recht können Sie:"
                  : language === "en"
                  ? "In accordance with applicable law, you may:"
                  : "Conformément à la législation en vigueur, vous pouvez :"}
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>{language === "de" ? "Zugang zu Ihren Daten verlangen" : language === "en" ? "Request access to your data" : "Demander l'accès à vos données"}</li>
                <li>{language === "de" ? "Deren Änderung oder Löschung verlangen" : language === "en" ? "Request their modification or deletion" : "Demander leur modification ou suppression"}</li>
              </ul>
              <p className="mt-3">
                {language === "de" ? "Für jede Anfrage:" : language === "en" ? "For any request:" : "Pour toute demande :"}
                <br />
                <a href="mailto:info_presenceetsourire@etik.com" className="text-[#6FA8B6] hover:underline">
                  info_presenceetsourire@etik.com
                </a>
              </p>
            </div>

            <div>
              <h2 className="font-heading text-xl text-[#2C3E50] mb-3">Cookies</h2>
              <p>
                {language === "de"
                  ? "Diese Website kann Cookies verwenden, um die Benutzererfahrung zu verbessern. Sie können diese über die Einstellungen Ihres Browsers ablehnen."
                  : language === "en"
                  ? "This site may use cookies to improve the user experience. You can refuse them via your browser settings."
                  : "Ce site peut utiliser des cookies pour améliorer l'expérience utilisateur. Vous pouvez les refuser via les paramètres de votre navigateur."}
              </p>
            </div>

            <div>
              <h2 className="font-heading text-xl text-[#2C3E50] mb-3">
                {language === "de" ? "Änderung" : language === "en" ? "Modification" : "Modification"}
              </h2>
              <p>
                {language === "de"
                  ? "Diese Richtlinie kann jederzeit geändert werden."
                  : language === "en"
                  ? "This policy may be modified at any time."
                  : "Cette politique peut être modifiée à tout moment."}
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
